/*======================================================================================================================
  * Bluebell: src/app/app.config.ts
  * Last Edited: 14/02/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { BrambleRouteReuseStrategy } from '@bramble/reuse-strategy';
import { environment } from '@environments/environment';
import { provideEffects } from '@ngrx/effects';
import { ActionReducer, provideState, provideStore } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { primengConfig } from '@app/primeng-config';
import { AccountEffects } from '@state/account/account.effects';
import * as fromAccount from '@state/account/account.reducer';
import * as Account from '@state/account/account.reducer';
import { AnnotationsEffects } from '@state/annotations/annotations.effects';
import * as Annotations from '@state/annotations/annotations.reducer';
import { AssemblyLibraryEffects } from '@state/assembly-libraries/assembly-library.effects';
import * as AssemblyLibrary from '@state/assembly-libraries/assembly-library.reducer';
import { AuthenticationEffects } from '@state/authentication/authentication.effects';
import * as fromAuth from '@state/authentication/authentication.reducer';
import * as Auth from '@state/authentication/authentication.reducer';
import { CameraSetsEffects } from '@state/camera-sets/camera-sets.effects';
import * as CameraSets from '@state/camera-sets/camera-sets.reducer';
import { FileXEffects } from '@state/file-exchange/file-exchange.effects';
import * as FileExchange from '@state/file-exchange/file-exchange.reducer';
import { ImageDeltasEffects } from '@state/image-deltas/image-deltas.effects';
import * as ImageDeltas from '@state/image-deltas/image-deltas.reducer';
import { KinematicModelEffects } from '@state/kinematic-models/kinematic-model.effects';
import * as KinematicModels from '@state/kinematic-models/kinematic-model.reducer';
import { KinematicSetupEffects } from '@state/kinematic-setups/kinematic-setup.effects';
import * as KinematicSetups from '@state/kinematic-setups/kinematic-setup.reducer';
import { MappingsEffects } from '@state/mappings/mappings.effects';
import * as Mappings from '@state/mappings/mappings.reducer';
import { MapEffects } from '@state/maps/map.effects';
import * as Maps from '@state/maps/map.reducer';
import { MethodologiesEffects } from '@state/methodologies/methodologies.effects';
import * as Methodologies from '@state/methodologies/methodologies.reducer';
import { MindMapsEffects } from '@state/mind-maps/mind-maps.effects';
import * as MindMaps from '@state/mind-maps/mind-maps.reducer';
import { PartLibraryEffects } from '@state/part-libraries/part-library.effects';
import * as PartLibraries from '@state/part-libraries/part-library.reducer';
import { PostproSettingsEffects } from '@state/postpro-settings/postpro-settings.effects';
import * as PostproSettings from '@state/postpro-settings/postpro-settings.reducer';
import { PostproEffects } from '@state/postpro/postpro.effects';
import * as Postpro from '@state/postpro/postpro.reducer';
import { ProgrammeEffects } from '@state/programmes/programme.effects';
import * as Programmes from '@state/programmes/programme.reducer';
import { ProjectEffects } from '@state/projects/project.effects';
import * as Projects from '@state/projects/project.reducer';
import { ReportEffects } from '@state/reports/reports.effects';
import * as Reports from '@state/reports/reports.reducer';
import { ResultsFilterEffects } from '@state/results-filters/results-filter.effects';
import * as ResultsFilters from '@state/results-filters/results-filter.reducer';
import { ResultsViewEffects } from '@state/results-view/results-view.effects';
import * as ResultsViews from '@state/results-view/results-view.reducer';
import { ResultsEffects } from '@state/results/results.effects';
import * as Results from '@state/results/results.reducer';
import { RunEffects } from '@state/runs/run.effects';
import * as Runs from '@state/runs/run.reducer';
import { SandboxEffects } from '@state/sandbox/sandbox.effects';
import * as Sandbox from '@state/sandbox/sandbox.reducer';
import { TasksEffects } from '@state/tasks/tasks.effects';
import * as Tasks from '@state/tasks/tasks.reducer';
import { TemplateCaseEffects } from '@state/template-cases/template-case.effects';
import * as TemplateCases from '@state/template-cases/template-case.reducer';
import { TextColumnsEffects } from '@state/text-columns/text-columns.effects';
import * as TextColumns from '@state/text-columns/text-columns.reducer';
import * as Viewer from '@state/viewer/viewer.reducer';
import { WorkflowsEffects } from '@state/workflows/workflows.effects';
import * as Workflows from '@state/workflows/workflows.reducer';
import { storeLogger } from '@zerops/ngrx-store-logger';
import { localStorageSync } from 'ngrx-store-localstorage';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';


export function logger(reducer: ActionReducer<any>): any {
  return storeLogger({ collapsed: true })(reducer);
}

export function localStorageReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({
    keys: [
      { [fromAuth.featureKey]: ['username', 'access_token', 'refresh_token', 'expires_in'] },
      { [fromAccount.featureKey]: ['userSettings', 'accountSettings', 'user'] },
    ],
    rehydrate: true
  })(reducer);
}

export const metaReducers = environment.production ? [localStorageReducer] : [logger, localStorageReducer];

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: BrambleRouteReuseStrategy,
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    providePrimeNG(primengConfig),

    // NgRx - Root State
    provideStore(undefined, { metaReducers }),
    provideState({ name: Account.featureKey, reducer: Account.reducer }),
    provideState({ name: Annotations.featureKey, reducer: Annotations.reducer }),
    provideState({ name: AssemblyLibrary.featureKey, reducer: AssemblyLibrary.reducer }),
    provideState({ name: Auth.featureKey, reducer: Auth.reducer }),
    provideState({ name: CameraSets.featureKey, reducer: CameraSets.reducer }),
    provideState({ name: FileExchange.featureKey, reducer: FileExchange.reducer }),
    provideState({ name: ImageDeltas.featureKey, reducer: ImageDeltas.reducer }),
    provideState({ name: KinematicModels.featureKey, reducer: KinematicModels.reducer }),
    provideState({ name: KinematicSetups.featureKey, reducer: KinematicSetups.reducer }),
    provideState({ name: Mappings.featureKey, reducer: Mappings.reducer }),
    provideState({ name: Maps.featureKey, reducer: Maps.reducer }),
    provideState({ name: Methodologies.featureKey, reducer: Methodologies.reducer }),
    provideState({ name: MindMaps.featureKey, reducer: MindMaps.reducer }),
    provideState({ name: PartLibraries.featureKey, reducer: PartLibraries.reducer }),
    provideState({ name: Postpro.featureKey, reducer: Postpro.reducer }),
    provideState({ name: PostproSettings.featureKey, reducer: PostproSettings.reducer }),
    provideState({ name: Programmes.featureKey, reducer: Programmes.reducer }),
    provideState({ name: Projects.featureKey, reducer: Projects.reducer }),
    provideState({ name: Reports.featureKey, reducer: Reports.reducer }),
    provideState({ name: Results.featureKey, reducer: Results.reducer }),
    provideState({ name: ResultsFilters.featureKey, reducer: ResultsFilters.reducer }),
    provideState({ name: ResultsViews.featureKey, reducer: ResultsViews.reducer }),
    provideState({ name: Runs.featureKey, reducer: Runs.reducer }),
    provideState({ name: Sandbox.featureKey, reducer: Sandbox.reducer }),
    provideState({ name: Tasks.featureKey, reducer: Tasks.reducer }),
    provideState({ name: TemplateCases.featureKey, reducer: TemplateCases.reducer }),
    provideState({ name: TextColumns.featureKey, reducer: TextColumns.reducer }),
    provideState({ name: Sandbox.featureKey, reducer: Sandbox.reducer }),
    provideState({ name: Viewer.featureKey, reducer: Viewer.reducer }),
    provideState({ name: Workflows.featureKey, reducer: Workflows.reducer }),
    provideEffects(
      // For Root
      AuthenticationEffects,
      AccountEffects,
      AssemblyLibraryEffects,
      CameraSetsEffects,
      FileXEffects,
      MappingsEffects,
      TasksEffects,
      ProjectEffects,
      ProgrammeEffects,
      PartLibraryEffects,
      RunEffects,
      ReportEffects,
      MapEffects,
      MethodologiesEffects,
      KinematicSetupEffects,
      KinematicModelEffects,
      TemplateCaseEffects,
      ResultsFilterEffects,
      PostproSettingsEffects,
      AnnotationsEffects,
      ImageDeltasEffects,
      PostproEffects,
      ResultsEffects,
      ResultsViewEffects,
      TextColumnsEffects,
      SandboxEffects,
      MindMapsEffects,
      WorkflowsEffects,
    ),
    MessageService,


    importProvidersFrom(
      NgxGoogleAnalyticsModule.forRoot('G-VXV2LJ4EY7'),
      NgxGoogleAnalyticsRouterModule,
      StoreDevtoolsModule.instrument({
        maxAge: 25, // Retains last 25 states
        logOnly: environment.production, // Restrict extension to log-only mode
      }),
    )
  ],
};
